import React, { useCallback, useEffect, useState } from 'react';
import { Container } from '@mui/material';
import { EventListContainer } from '../../components/ScreenContainer/EventListContainer/EventListContainer';
import { MetaTags } from '../../components/Metatags/Metatags';
import { filterSelector } from '../Audition/selector';
import { useSelector } from 'react-redux';
import { getFilters } from '../../redux/appConfigurator/action';
import { filterEvents, getEvents, setEventFilter } from '../../redux/events/action';
import { allEventsSelector, loadingEventSelector, selectedFiltersSelectors } from './selector';
import { IEventFilter } from '../../redux/events/types';
import { usePagination } from '../../hook/usePagination';
import { CustomPagination } from '../../components/Tables/Pagination';
import { useDispatchPromise } from '../../utils/AppUtils';
import { isEmpty } from '../../utils/lodash';

const parseFilter = (filters: IEventFilter) => {
    const parsed: Record<string, any> = {};
    Object.keys(filters).forEach(key => {
        const typedKey = key as keyof IEventFilter;
        const { value } = filters[typedKey];
        switch (typedKey) {
            case 'with_fee':
                parsed[key] = value === 'with fee' ? true : value === 'free' ? false : undefined;
                break;
            case 'age_restriction':
                parsed['min_age'] = value;

                break;
            case 'location':
                if (typeof value === 'object' && 'coordinates' in value) {
                    parsed[key] = value.coordinates;
                }
                break;
            default:
                parsed[key] = filters[key as keyof IEventFilter].value;
                break;
        }
    });
    return parsed;
};

const Events = () => {
    const filters = useSelector(filterSelector);
    const events = useSelector(allEventsSelector);
    const loading = useSelector(loadingEventSelector);
    const selectedFilters = useSelector(selectedFiltersSelectors);
    const dispatch = useDispatchPromise();
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination(1, 12);
    const [filterMode, setFilerMode] = useState(false);

    const getAllEvents = useCallback(
        async (page?: number, limit?: number) => {
            if (!filterMode) {
                dispatch(getFilters({ coll_type: 'event' }));
                const response: any = await dispatch(
                    getEvents({ limit: limit ?? pagination.rows, page: page ?? pagination.page }),
                );
                if (response.currentPage) {
                    const { currentPage, pageSize, totalCount } = response;
                    setPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount: totalCount,
                    });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pagination.page, pagination.rows, filterMode],
    );

    const handleOnApply = useCallback(
        async (payload: IEventFilter) => {
            if (filterMode) {
                const parsedPayload = parseFilter(payload);
                dispatch(setEventFilter(payload));

                const response: any = await dispatch(
                    filterEvents({
                        ...parsedPayload,
                    }),
                );

                if (response.currentPage) {
                    const { currentPage, pageSize, totalCount } = response;
                    setPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount: totalCount,
                    });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pagination.page, pagination.rows, filterMode],
    );

    const handleClearFilters = () => {
        dispatch(setEventFilter(undefined));
        getAllEvents(1, pagination.rows);
        setFilerMode(false);
    };

    useEffect(() => {
        getAllEvents();
    }, [getAllEvents]);
    return (
        <MetaTags title={`Thspian | Live Events`} description={'All Live Events'}>
            <Container>
                {!isEmpty(events) && (
                    <EventListContainer
                        onApply={handleOnApply}
                        filter={filters}
                        events={events}
                        onFilterAdjusted={active => {
                            /** Sets when we start tapping on filters */
                            if (active) {
                                setFilerMode(active);
                            }
                        }}
                        loading={loading}
                        selectedFilters={selectedFilters}
                        totalEvents={pagination.totalCount}
                        deleteFilter={handleOnApply}
                        onClearFilter={handleClearFilters}
                        paginationContainer={
                            <CustomPagination
                                pagination={pagination}
                                rowsPerPageOptions={[12, 24, 48]}
                                onRowsChange={handleRowsChange}
                                onPageChange={handlePageChange}
                            />
                        }
                    />
                )}
            </Container>
        </MetaTags>
    );
};

export { Events };
